import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { MdSettings, MdAdd, MdDelete, MdExpandMore, MdExpandLess } from 'react-icons/md';
import { api } from '../services/api';
import Toast from '../components/Toast';

export default function Configuracoes() {
  const { currentUser, onSettingsUpdate } = useOutletContext();
  const [activeTab, setActiveTab] = useState('clinic');
  const [toast, setToast] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedDays, setExpandedDays] = useState({});
  const [loadingCep, setLoadingCep] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [logoPreview, setLogoPreview] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [settings, setSettings] = useState({
    clinicName: '',
    doctorName: '',
    specialty: '',
    crm: '',
    phone: '',
    email: '',
    logo: '',
    theme: {
      primaryColor: '#511c60',
      buttonColor: '#511c60'
    },
    address: {
      street: '',
      number: '',
      complement: '',
      neighborhood: '',
      city: '',
      state: '',
      zipCode: ''
    },
    scheduleSettings: {
      startTime: '08:00',
      endTime: '18:00',
      slotDuration: 30,
      defaultBreakTimes: [],
      workDays: {
        monday: { enabled: true, startTime: '08:00', endTime: '18:00', breakTimes: [] },
        tuesday: { enabled: true, startTime: '08:00', endTime: '18:00', breakTimes: [] },
        wednesday: { enabled: true, startTime: '08:00', endTime: '18:00', breakTimes: [] },
        thursday: { enabled: true, startTime: '08:00', endTime: '18:00', breakTimes: [] },
        friday: { enabled: true, startTime: '08:00', endTime: '18:00', breakTimes: [] },
        saturday: { enabled: false },
        sunday: { enabled: false }
      },
      weekendSchedule: {
        enabled: false,
        startTime: '09:00',
        endTime: '13:00'
      }
    }
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/settings');
      if (response.data) {
        // Mesclar com os valores padrão para garantir que todas as propriedades existam
        const mergedSettings = {
          ...settings,
          ...response.data,
          scheduleSettings: {
            ...settings.scheduleSettings,
            ...response.data.scheduleSettings,
            workDays: {
              ...settings.scheduleSettings.workDays,
              ...response.data.scheduleSettings?.workDays
            }
          },
          address: {
            ...settings.address,
            ...response.data.address
          }
        };
        setSettings(mergedSettings);
      }
    } catch (error) {
      console.error('Erro ao carregar configurações:', error);
      setToast({ message: 'Erro ao carregar configurações', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      await api.put('/settings', settings);
      setToast({ message: 'Configurações salvas com sucesso!', type: 'success' });
      // Recarregar settings no App para aplicar o tema
      if (onSettingsUpdate) {
        onSettingsUpdate();
      }
    } catch (error) {
      console.error('Erro ao salvar configurações:', error);
      setToast({ message: 'Erro ao salvar configurações', type: 'error' });
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleAddressChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      address: { ...prev.address, [field]: value }
    }));
  };

  const handleScheduleChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      scheduleSettings: { ...prev.scheduleSettings, [field]: value }
    }));
  };

  const handleWorkDayChange = (day, value) => {
    setSettings(prev => ({
      ...prev,
      scheduleSettings: {
        ...prev.scheduleSettings,
        workDays: { 
          ...prev.scheduleSettings.workDays, 
          [day]: typeof value === 'boolean' ? value : prev.scheduleSettings.workDays[day]
        }
      }
    }));
  };

  const handleWorkDayTimeChange = (day, field, value) => {
    setSettings(prev => ({
      ...prev,
      scheduleSettings: {
        ...prev.scheduleSettings,
        workDays: {
          ...prev.scheduleSettings.workDays,
          [day]: {
            ...prev.scheduleSettings.workDays[day],
            [field]: value
          }
        }
      }
    }));
  };

  const handleWeekendScheduleChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      scheduleSettings: {
        ...prev.scheduleSettings,
        weekendSchedule: { ...prev.scheduleSettings.weekendSchedule, [field]: value }
      }
    }));
  };

  const addDefaultBreakTime = () => {
    setSettings(prev => ({
      ...prev,
      scheduleSettings: {
        ...prev.scheduleSettings,
        defaultBreakTimes: [
          ...(prev.scheduleSettings.defaultBreakTimes || []),
          { startTime: '12:00', endTime: '13:30', description: 'Almoço' }
        ]
      }
    }));
  };

  const removeDefaultBreakTime = (index) => {
    setSettings(prev => ({
      ...prev,
      scheduleSettings: {
        ...prev.scheduleSettings,
        defaultBreakTimes: prev.scheduleSettings.defaultBreakTimes.filter((_, i) => i !== index)
      }
    }));
  };

  const updateDefaultBreakTime = (index, field, value) => {
    setSettings(prev => ({
      ...prev,
      scheduleSettings: {
        ...prev.scheduleSettings,
        defaultBreakTimes: prev.scheduleSettings.defaultBreakTimes.map((bt, i) =>
          i === index ? { ...bt, [field]: value } : bt
        )
      }
    }));
  };

  const addBreakTimeToDay = (dayKey) => {
    setSettings(prev => {
      const dayConfig = prev.scheduleSettings.workDays[dayKey];
      const defaultBreak = prev.scheduleSettings.defaultBreakTimes?.[0] || { startTime: '12:00', endTime: '13:30', description: 'Almoço' };
      return {
        ...prev,
        scheduleSettings: {
          ...prev.scheduleSettings,
          workDays: {
            ...prev.scheduleSettings.workDays,
            [dayKey]: {
              ...dayConfig,
              breakTimes: [
                ...(dayConfig.breakTimes || []),
                { ...defaultBreak }
              ]
            }
          }
        }
      };
    });
  };

  const removeBreakTimeFromDay = (dayKey, index) => {
    setSettings(prev => {
      const dayConfig = prev.scheduleSettings.workDays[dayKey];
      return {
        ...prev,
        scheduleSettings: {
          ...prev.scheduleSettings,
          workDays: {
            ...prev.scheduleSettings.workDays,
            [dayKey]: {
              ...dayConfig,
              breakTimes: dayConfig.breakTimes.filter((_, i) => i !== index)
            }
          }
        }
      };
    });
  };

  const updateBreakTimeInDay = (dayKey, index, field, value) => {
    setSettings(prev => {
      const dayConfig = prev.scheduleSettings.workDays[dayKey];
      return {
        ...prev,
        scheduleSettings: {
          ...prev.scheduleSettings,
          workDays: {
            ...prev.scheduleSettings.workDays,
            [dayKey]: {
              ...dayConfig,
              breakTimes: dayConfig.breakTimes.map((bt, i) =>
                i === index ? { ...bt, [field]: value } : bt
              )
            }
          }
        }
      };
    });
  };

  const formatPhone = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 10) {
      return numbers.replace(/(\d{2})(\d{4})(\d{0,4})/, '($1) $2-$3');
    }
    return numbers.replace(/(\d{2})(\d{5})(\d{0,4})/, '($1) $2-$3');
  };

  const handlePhoneChange = (value) => {
    const formatted = formatPhone(value);
    handleChange('phone', formatted);
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailChange = (value) => {
    handleChange('email', value);
    if (value && !validateEmail(value)) {
      setEmailError('E-mail inválido');
    } else {
      setEmailError('');
    }
  };

  const fetchAddressByCep = async (cep) => {
    const cleanCep = cep.replace(/\D/g, '');
    if (cleanCep.length !== 8) return;

    setLoadingCep(true);
    try {
      const response = await fetch(`https://viacep.com.br/ws/${cleanCep}/json/`);
      const data = await response.json();
      
      if (data.erro) {
        setToast({ message: 'CEP não encontrado', type: 'error' });
        return;
      }

      setSettings(prev => ({
        ...prev,
        address: {
          ...prev.address,
          zipCode: cep,
          street: data.logradouro || '',
          neighborhood: data.bairro || '',
          city: data.localidade || '',
          state: data.uf || ''
        }
      }));
    } catch (error) {
      console.error('Erro ao buscar CEP:', error);
      setToast({ message: 'Erro ao buscar CEP', type: 'error' });
    } finally {
      setLoadingCep(false);
    }
  };

  const handleCepChange = (value) => {
    const numbers = value.replace(/\D/g, '');
    const formatted = numbers.replace(/(\d{5})(\d{0,3})/, '$1-$2');
    handleAddressChange('zipCode', formatted);
    
    if (numbers.length === 8) {
      fetchAddressByCep(formatted);
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setToast({ message: 'Por favor, selecione uma imagem', type: 'error' });
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result);
      handleChange('logo', reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleThemeColorChange = (field, color) => {
    setSettings(prev => ({
      ...prev,
      theme: {
        ...prev.theme,
        [field]: color
      }
    }));
  };

  const applyToAllDays = () => {
    const globalStartTime = settings.scheduleSettings.startTime;
    const globalEndTime = settings.scheduleSettings.endTime;
    const globalBreakTimes = settings.scheduleSettings.defaultBreakTimes || [];

    setSettings(prev => {
      const updatedWorkDays = {};
      Object.keys(prev.scheduleSettings.workDays).forEach(dayKey => {
        const dayConfig = prev.scheduleSettings.workDays[dayKey];
        const isEnabled = typeof dayConfig === 'boolean' ? dayConfig : dayConfig?.enabled !== false;
        
        if (isEnabled) {
          updatedWorkDays[dayKey] = {
            enabled: true,
            startTime: globalStartTime,
            endTime: globalEndTime,
            breakTimes: globalBreakTimes.map(bt => ({ ...bt }))
          };
        } else {
          updatedWorkDays[dayKey] = dayConfig;
        }
      });

      return {
        ...prev,
        scheduleSettings: {
          ...prev.scheduleSettings,
          workDays: updatedWorkDays
        }
      };
    });

    setToast({ message: 'Horários aplicados a todos os dias habilitados!', type: 'success' });
  };

  return (
    <div className="patients-card" style={{ paddingTop: 44, paddingBottom: 56, color: '#1C1C1E' }}>
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28 }}>
        <div>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 7,
            background: '#FBE0E5', color: '#9F4A60',
            padding: '4px 10px', borderRadius: 999,
            fontSize: 11, letterSpacing: '-0.005em', fontWeight: 600, whiteSpace: 'nowrap',
          }}>
            <span style={{ width: 5, height: 5, borderRadius: 999, background: '#C97A8F' }} />
            Configurações da clínica
          </div>
          <h1 style={{ fontWeight: 700, fontSize: 38, lineHeight: 1.18, letterSpacing: '-0.035em', margin: '10px 0 0', color: '#1C1C1E' }}>
            Configurações
          </h1>
          <div style={{ marginTop: 6, fontSize: 14, color: '#6E6E73', letterSpacing: '-0.005em' }}>
            Gerencie as informações da clínica e da agenda
          </div>
        </div>
        <button className="mac-button" onClick={handleSave} style={{ marginTop: 6 }}>
          Salvar configurações
        </button>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '12px', marginTop: '24px', borderBottom: '1px solid rgba(0,0,0,0.1)' }}>
        <button
          onClick={() => setActiveTab('clinic')}
          style={{
            padding: '12px 24px',
            background: 'transparent',
            border: 'none',
            borderBottom: activeTab === 'clinic' ? '2px solid #511c60' : '2px solid transparent',
            color: activeTab === 'clinic' ? '#511c60' : '#8E8E93',
            fontWeight: 600,
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
        >
          Dados da Clínica
        </button>
        <button
          onClick={() => setActiveTab('schedule')}
          style={{
            padding: '12px 24px',
            background: 'transparent',
            border: 'none',
            borderBottom: activeTab === 'schedule' ? '2px solid #511c60' : '2px solid transparent',
            color: activeTab === 'schedule' ? '#511c60' : '#8E8E93',
            fontWeight: 600,
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
        >
          Configuração da Agenda
        </button>
        <button
          onClick={() => setActiveTab('personalization')}
          style={{
            padding: '12px 24px',
            background: 'transparent',
            border: 'none',
            borderBottom: activeTab === 'personalization' ? '2px solid #511c60' : '2px solid transparent',
            color: activeTab === 'personalization' ? '#511c60' : '#8E8E93',
            fontWeight: 600,
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
        >
          Personalização
        </button>
      </div>

      {/* Content */}
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px', marginTop: '24px' }}>
          <p style={{ color: '#8E8E93' }}>Carregando configurações...</p>
        </div>
      ) : (
        <div style={{ marginTop: '24px' }}>
          {activeTab === 'clinic' && (
            <div className="form-card">
                  <h2 style={{ marginTop: 0 }}>Informações da Clínica</h2>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                      <label style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <span style={{ fontSize: '0.9rem', color: '#6e6e73', fontWeight: 500 }}>Nome da Clínica</span>
                        <input
                          className="mac-input"
                          value={settings.clinicName}
                          onChange={(e) => handleChange('clinicName', e.target.value)}
                          placeholder="Clínica Exemplo"
                        />
                      </label>
                      <label style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <span style={{ fontSize: '0.9rem', color: '#6e6e73', fontWeight: 500 }}>Nome do Médico</span>
                        <input
                          className="mac-input"
                          value={settings.doctorName}
                          onChange={(e) => handleChange('doctorName', e.target.value)}
                          placeholder="Dr. João Silva"
                        />
                      </label>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                      <label style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <span style={{ fontSize: '0.9rem', color: '#6e6e73', fontWeight: 500 }}>Especialidade</span>
                        <input
                          className="mac-input"
                          value={settings.specialty}
                          onChange={(e) => handleChange('specialty', e.target.value)}
                          placeholder="Ex: Clínica Geral, Pediatria, etc."
                        />
                      </label>
                      <label style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <span style={{ fontSize: '0.9rem', color: '#6e6e73', fontWeight: 500 }}>CRM</span>
                        <input
                          className="mac-input"
                          value={settings.crm}
                          onChange={(e) => handleChange('crm', e.target.value)}
                          placeholder="123456/PR"
                        />
                      </label>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                      <label style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <span style={{ fontSize: '0.9rem', color: '#6e6e73', fontWeight: 500 }}>Telefone</span>
                        <input
                          className="mac-input"
                          value={settings.phone}
                          onChange={(e) => handlePhoneChange(e.target.value)}
                          placeholder=""
                          maxLength="15"
                        />
                      </label>
                      <label style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <span style={{ fontSize: '0.9rem', color: '#6e6e73', fontWeight: 500 }}>E-mail</span>
                        <input
                          className="mac-input"
                          type="email"
                          value={settings.email}
                          onChange={(e) => handleEmailChange(e.target.value)}
                          placeholder="contato@clinica.com"
                          style={{ borderColor: emailError ? '#FF3B30' : undefined }}
                        />
                        {emailError && (
                          <span style={{ fontSize: '0.8rem', color: '#FF3B30' }}>{emailError}</span>
                        )}
                      </label>
                    </div>

                    <h3 style={{ marginTop: '16px', marginBottom: '8px' }}>Endereço</h3>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '16px' }}>
                      <label style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <span style={{ fontSize: '0.9rem', color: '#6e6e73', fontWeight: 500 }}>CEP</span>
                        <input
                          className="mac-input"
                          value={settings.address.zipCode}
                          onChange={(e) => handleCepChange(e.target.value)}
                          placeholder="00000-000"
                          maxLength="9"
                          disabled={loadingCep}
                          style={{ opacity: loadingCep ? 0.6 : 1 }}
                        />
                        {loadingCep && (
                          <span style={{ fontSize: '0.8rem', color: '#6e6e73' }}>Buscando endereço...</span>
                        )}
                      </label>
                      <div></div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '16px' }}>
                      <label style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <span style={{ fontSize: '0.9rem', color: '#6e6e73', fontWeight: 500 }}>Rua</span>
                        <input
                          className="mac-input"
                          value={settings.address.street}
                          onChange={(e) => handleAddressChange('street', e.target.value)}
                          placeholder="Rua Exemplo"
                        />
                      </label>
                      <label style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <span style={{ fontSize: '0.9rem', color: '#6e6e73', fontWeight: 500 }}>Número</span>
                        <input
                          className="mac-input"
                          value={settings.address.number}
                          onChange={(e) => handleAddressChange('number', e.target.value)}
                          placeholder="123"
                        />
                      </label>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                      <label style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <span style={{ fontSize: '0.9rem', color: '#6e6e73', fontWeight: 500 }}>Complemento</span>
                        <input
                          className="mac-input"
                          value={settings.address.complement}
                          onChange={(e) => handleAddressChange('complement', e.target.value)}
                          placeholder="Sala 101"
                        />
                      </label>
                      <label style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <span style={{ fontSize: '0.9rem', color: '#6e6e73', fontWeight: 500 }}>Bairro</span>
                        <input
                          className="mac-input"
                          value={settings.address.neighborhood}
                          onChange={(e) => handleAddressChange('neighborhood', e.target.value)}
                          placeholder="Centro"
                        />
                      </label>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '16px' }}>
                      <label style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <span style={{ fontSize: '0.9rem', color: '#6e6e73', fontWeight: 500 }}>Cidade</span>
                        <input
                          className="mac-input"
                          value={settings.address.city}
                          onChange={(e) => handleAddressChange('city', e.target.value)}
                          placeholder="Maringá"
                        />
                      </label>
                      <label style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <span style={{ fontSize: '0.9rem', color: '#6e6e73', fontWeight: 500 }}>Estado</span>
                        <input
                          className="mac-input"
                          value={settings.address.state}
                          onChange={(e) => handleAddressChange('state', e.target.value)}
                          placeholder="PR"
                          maxLength="2"
                        />
                      </label>
                    </div>
                  </div>
                </div>
            )}

            {activeTab === 'schedule' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  {/* Horários */}
                  <div className="form-card" style={{ overflow: 'hidden' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                      <h2 style={{ margin: 0 }}>Horários de Atendimento Global</h2>
                      <button
                        className="mac-button"
                        onClick={applyToAllDays}
                        style={{ fontSize: '0.9rem', padding: '8px 16px' }}
                      >
                        Aplicar
                      </button>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
                        <label style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          <span style={{ fontSize: '0.9rem', color: '#6e6e73', fontWeight: 500 }}>Início do Expediente</span>
                          <input
                            className="mac-input"
                            type="time"
                            value={settings.scheduleSettings.startTime}
                            onChange={(e) => handleScheduleChange('startTime', e.target.value)}
                          />
                        </label>
                        <label style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          <span style={{ fontSize: '0.9rem', color: '#6e6e73', fontWeight: 500 }}>Fim do Expediente</span>
                          <input
                            className="mac-input"
                            type="time"
                            value={settings.scheduleSettings.endTime}
                            onChange={(e) => handleScheduleChange('endTime', e.target.value)}
                          />
                        </label>
                        <label style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          <span style={{ fontSize: '0.9rem', color: '#6e6e73', fontWeight: 500 }}>Duração da Consulta (min)</span>
                          <select
                            className="mac-input"
                            value={settings.scheduleSettings.slotDuration}
                            onChange={(e) => handleScheduleChange('slotDuration', parseInt(e.target.value))}
                          >
                            <option value="15">15 minutos</option>
                            <option value="20">20 minutos</option>
                            <option value="30">30 minutos</option>
                            <option value="45">45 minutos</option>
                            <option value="60">60 minutos</option>
                          </select>
                        </label>
                      </div>

                      {/* Intervalos Padrão */}
                      <div style={{ marginTop: '16px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                          <h3 style={{ margin: 0, fontSize: '1rem', color: '#1c1c1e' }}>Intervalos Padrão</h3>
                          <button
                            className="ghost-button"
                            onClick={addDefaultBreakTime}
                            style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.9rem' }}
                          >
                            <MdAdd size={18} />
                            Adicionar Intervalo
                          </button>
                        </div>
                        <p style={{ fontSize: '0.85rem', color: '#6e6e73', marginTop: 0, marginBottom: '12px' }}>
                          Estes intervalos serão aplicados automaticamente ao habilitar um dia da semana.
                        </p>
                        {(settings.scheduleSettings.defaultBreakTimes || []).length > 0 ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {settings.scheduleSettings.defaultBreakTimes.map((breakTime, index) => (
                              <div key={index} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 2fr auto', gap: '12px', alignItems: 'end' }}>
                                <input
                                  className="mac-input"
                                  type="time"
                                  value={breakTime.startTime}
                                  onChange={(e) => updateDefaultBreakTime(index, 'startTime', e.target.value)}
                                  placeholder="Início"
                                  style={{ fontSize: '0.9rem' }}
                                />
                                <input
                                  className="mac-input"
                                  type="time"
                                  value={breakTime.endTime}
                                  onChange={(e) => updateDefaultBreakTime(index, 'endTime', e.target.value)}
                                  placeholder="Fim"
                                  style={{ fontSize: '0.9rem' }}
                                />
                                <input
                                  className="mac-input"
                                  value={breakTime.description}
                                  onChange={(e) => updateDefaultBreakTime(index, 'description', e.target.value)}
                                  placeholder="Ex: Almoço"
                                  style={{ fontSize: '0.9rem' }}
                                />
                                <button
                                  className="ghost-button"
                                  onClick={() => removeDefaultBreakTime(index)}
                                  style={{ color: '#FF3B30', padding: '8px' }}
                                >
                                  <MdDelete size={18} />
                                </button>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p style={{ fontSize: '0.85rem', color: '#8E8E93', fontStyle: 'italic', textAlign: 'center', padding: '12px' }}>
                            Nenhum intervalo padrão configurado
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Dias da Semana */}
                  <div className="form-card" style={{ overflow: 'hidden' }}>
                    <h2 style={{ marginTop: 0 }}>Dias e Horários de Atendimento</h2>
                    <p style={{ fontSize: '0.9rem', color: '#6e6e73', marginBottom: '16px' }}>
                      Configure os horários e intervalos específicos para cada dia da semana.
                    </p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {[
                        { key: 'monday', label: 'Segunda-feira' },
                        { key: 'tuesday', label: 'Terça-feira' },
                        { key: 'wednesday', label: 'Quarta-feira' },
                        { key: 'thursday', label: 'Quinta-feira' },
                        { key: 'friday', label: 'Sexta-feira' },
                        { key: 'saturday', label: 'Sábado' },
                        { key: 'sunday', label: 'Domingo' }
                      ].map(day => {
                        const dayConfig = settings.scheduleSettings.workDays[day.key];
                        const isEnabled = typeof dayConfig === 'boolean' ? dayConfig : dayConfig?.enabled !== false;
                        const startTime = typeof dayConfig === 'object' ? dayConfig.startTime : settings.scheduleSettings.startTime;
                        const endTime = typeof dayConfig === 'object' ? dayConfig.endTime : settings.scheduleSettings.endTime;
                        const breakTimes = typeof dayConfig === 'object' ? (dayConfig.breakTimes || []) : [];
                        
                        return (
                          <div
                            key={day.key}
                            style={{
                              padding: '12px 16px',
                              borderRadius: '8px',
                              background: isEnabled ? 'rgba(81, 28, 96, 0.05)' : 'rgba(0,0,0,0.02)',
                              border: `1px solid ${isEnabled ? 'rgba(81, 28, 96, 0.2)' : 'rgba(0,0,0,0.05)'}`,
                              transition: 'all 0.2s'
                            }}
                          >
                            {/* Cabeçalho do dia */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: isEnabled && expandedDays[day.key] ? '12px' : '0' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: '160px' }}>
                                <input
                                  type="checkbox"
                                  checked={isEnabled}
                                  onChange={(e) => {
                                    const checked = e.target.checked;
                                    setSettings(prev => ({
                                      ...prev,
                                      scheduleSettings: {
                                        ...prev.scheduleSettings,
                                        workDays: {
                                          ...prev.scheduleSettings.workDays,
                                          [day.key]: checked ? {
                                            enabled: true,
                                            startTime: prev.scheduleSettings.startTime,
                                            endTime: prev.scheduleSettings.endTime,
                                            breakTimes: (prev.scheduleSettings.defaultBreakTimes || []).map(bt => ({ ...bt }))
                                          } : { enabled: false }
                                        }
                                      }
                                    }));
                                  }}
                                  style={{ margin: 0, cursor: 'pointer' }}
                                />
                                <span style={{ fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer' }} onClick={(e) => {
                                  const checkbox = e.target.previousElementSibling;
                                  checkbox.click();
                                }}>{day.label}</span>
                              </div>
                              
                              {isEnabled && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                  <span style={{ fontSize: '0.85rem', color: '#6e6e73' }}>
                                    {startTime} - {endTime}
                                  </span>
                                  <button
                                    className="ghost-button"
                                    onClick={() => setExpandedDays(prev => ({ ...prev, [day.key]: !prev[day.key] }))}
                                    style={{ padding: '4px', display: 'flex', alignItems: 'center' }}
                                  >
                                    {expandedDays[day.key] ? <MdExpandLess size={20} /> : <MdExpandMore size={20} />}
                                  </button>
                                </div>
                              )}
                              {!isEnabled && (
                                <span style={{ fontSize: '0.85rem', color: '#8E8E93', fontStyle: 'italic' }}>
                                  Sem atendimento
                                </span>
                              )}
                            </div>

                            {/* Detalhes do dia (colapsável) */}
                            {isEnabled && expandedDays[day.key] && (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '16px', alignItems: 'end' }}>
                                  <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                    <span style={{ fontSize: '0.8rem', color: '#6e6e73', fontWeight: 500 }}>Início</span>
                                    <input
                                      className="mac-input"
                                      type="time"
                                      value={startTime}
                                      onChange={(e) => {
                                        setSettings(prev => ({
                                          ...prev,
                                          scheduleSettings: {
                                            ...prev.scheduleSettings,
                                            workDays: {
                                              ...prev.scheduleSettings.workDays,
                                              [day.key]: {
                                                ...dayConfig,
                                                enabled: true,
                                                startTime: e.target.value,
                                                breakTimes: breakTimes
                                              }
                                            }
                                          }
                                        }));
                                      }}
                                    />
                                  </label>
                                  <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                    <span style={{ fontSize: '0.8rem', color: '#6e6e73', fontWeight: 500 }}>Fim</span>
                                    <input
                                      className="mac-input"
                                      type="time"
                                      value={endTime}
                                      onChange={(e) => {
                                        setSettings(prev => ({
                                          ...prev,
                                          scheduleSettings: {
                                            ...prev.scheduleSettings,
                                            workDays: {
                                              ...prev.scheduleSettings.workDays,
                                              [day.key]: {
                                                ...dayConfig,
                                                enabled: true,
                                                endTime: e.target.value,
                                                breakTimes: breakTimes
                                              }
                                            }
                                          }
                                        }));
                                      }}
                                    />
                                  </label>
                                  <button
                                    className="ghost-button"
                                    onClick={() => addBreakTimeToDay(day.key)}
                                    style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.85rem', whiteSpace: 'nowrap' }}
                                  >
                                    <MdAdd size={18} />
                                    Intervalo
                                  </button>
                                </div>

                                {/* Intervalos do dia */}
                                {breakTimes.length > 0 && (
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '8px' }}>
                                    <div style={{ fontSize: '0.85rem', color: '#6e6e73', fontWeight: 500, marginBottom: '4px' }}>
                                      Intervalos:
                                    </div>
                                    {breakTimes.map((breakTime, index) => (
                                      <div key={index} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 2fr auto', gap: '12px', alignItems: 'end' }}>
                                        <input
                                          className="mac-input"
                                          type="time"
                                          value={breakTime.startTime}
                                          onChange={(e) => updateBreakTimeInDay(day.key, index, 'startTime', e.target.value)}
                                          placeholder="Início"
                                          style={{ fontSize: '0.85rem' }}
                                        />
                                        <input
                                          className="mac-input"
                                          type="time"
                                          value={breakTime.endTime}
                                          onChange={(e) => updateBreakTimeInDay(day.key, index, 'endTime', e.target.value)}
                                          placeholder="Fim"
                                          style={{ fontSize: '0.85rem' }}
                                        />
                                        <input
                                          className="mac-input"
                                          value={breakTime.description}
                                          onChange={(e) => updateBreakTimeInDay(day.key, index, 'description', e.target.value)}
                                          placeholder="Ex: Almoço"
                                          style={{ fontSize: '0.85rem' }}
                                        />
                                        <button
                                          className="ghost-button"
                                          onClick={() => removeBreakTimeFromDay(day.key, index)}
                                          style={{ color: '#FF3B30', padding: '8px' }}
                                        >
                                          <MdDelete size={18} />
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
            )}

            {activeTab === 'personalization' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                {/* Logotipo e Tema em Grid */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '20px' }}>
                  {/* Logotipo */}
                  <div className="form-card">
                    <h2 style={{ marginTop: 0, fontSize: '1.1rem' }}>Logotipo</h2>
                    <p style={{ fontSize: '0.85rem', color: '#6e6e73', marginBottom: '12px' }}>
                      Upload do logo da clínica
                    </p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'center' }}>
                      <div style={{
                        width: '120px',
                        height: '120px',
                        borderRadius: '12px',
                        border: '2px dashed rgba(0,0,0,0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                        background: 'rgba(0,0,0,0.02)'
                      }}>
                        {(logoPreview || settings.logo) ? (
                          <img 
                            src={logoPreview || settings.logo} 
                            alt="Logo" 
                            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                          />
                        ) : (
                          <span style={{ color: '#8E8E93', fontSize: '0.85rem', textAlign: 'center', padding: '12px' }}>
                            Sem logo
                          </span>
                        )}
                      </div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleLogoUpload}
                        style={{ display: 'none' }}
                        id="logo-upload"
                      />
                      <label htmlFor="logo-upload" style={{ width: '100%' }}>
                        <button
                          type="button"
                          className="mac-button"
                          onClick={() => document.getElementById('logo-upload').click()}
                          style={{ width: '100%', padding: '10px', fontSize: '0.9rem' }}
                        >
                          Escolher Imagem
                        </button>
                      </label>
                      <p style={{ fontSize: '0.8rem', color: '#6e6e73', margin: 0, textAlign: 'center' }}>
                        PNG, JPG, SVG<br />
                        200x200px
                      </p>
                    </div>
                  </div>

                  {/* Tema de Cores */}
                  <div className="form-card">
                    <h2 style={{ marginTop: 0, fontSize: '1.1rem' }}>Tema de Cores</h2>
                    <p style={{ fontSize: '0.85rem', color: '#6e6e73', marginBottom: '12px' }}>
                      Personalize as cores do sistema
                    </p>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                      <div>
                        <label style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          <span style={{ fontSize: '0.85rem', color: '#6e6e73', fontWeight: 500 }}>Cor Principal</span>
                          <input
                            type="color"
                            value={settings.theme?.primaryColor || '#511c60'}
                            onChange={(e) => handleThemeColorChange('primaryColor', e.target.value)}
                            style={{
                              width: '100%',
                              height: '80px',
                              border: '1px solid rgba(0,0,0,0.1)',
                              borderRadius: '8px',
                              cursor: 'pointer'
                            }}
                          />
                          <input
                            type="text"
                            className="mac-input"
                            value={settings.theme?.primaryColor || '#511c60'}
                            onChange={(e) => handleThemeColorChange('primaryColor', e.target.value)}
                            placeholder="#511c60"
                            maxLength="7"
                            style={{ padding: '8px 12px', fontSize: '0.9rem', textAlign: 'center' }}
                          />
                        </label>
                        <div style={{ marginTop: '10px', padding: '12px', borderRadius: '8px', background: settings.theme?.primaryColor || '#511c60', color: 'white', textAlign: 'center', fontWeight: 600, fontSize: '0.85rem' }}>
                          Preview
                        </div>
                      </div>

                      <div>
                        <label style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          <span style={{ fontSize: '0.85rem', color: '#6e6e73', fontWeight: 500 }}>Cor dos Botões</span>
                          <input
                            type="color"
                            value={settings.theme?.buttonColor || '#511c60'}
                            onChange={(e) => handleThemeColorChange('buttonColor', e.target.value)}
                            style={{
                              width: '100%',
                              height: '80px',
                              border: '1px solid rgba(0,0,0,0.1)',
                              borderRadius: '8px',
                              cursor: 'pointer'
                            }}
                          />
                          <input
                            type="text"
                            className="mac-input"
                            value={settings.theme?.buttonColor || '#511c60'}
                            onChange={(e) => handleThemeColorChange('buttonColor', e.target.value)}
                            placeholder="#511c60"
                            maxLength="7"
                            style={{ padding: '8px 12px', fontSize: '0.9rem', textAlign: 'center' }}
                          />
                        </label>
                        <button
                          className="mac-button"
                          style={{ 
                            marginTop: '10px', 
                            width: '100%',
                            background: settings.theme?.buttonColor || '#511c60',
                            borderColor: settings.theme?.buttonColor || '#511c60',
                            padding: '12px',
                            fontSize: '0.85rem'
                          }}
                        >
                          Preview
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
    </div>
  );
}
